import React, { FC, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  FormText,
  Input,
  Label,
  Row,
} from 'reactstrap';
import { passwordScore } from '../../../shared/user/password';
import {
  PageHeader,
  PasswordChangeInput,
  RichMarkdownIt,
  UserStateComponent,
} from '../../components';
import { useTranslation } from '../../translations';
import {
  useCheckboxState,
  useConvention,
  useFetcher,
  useForm,
  useInputState,
  useUserSafe,
} from '../../utils';

export const EditAccountComponent: FC = () => {
  const { ts } = useTranslation();
  const convention = useConvention();
  const user = useUserSafe();
  const [username, setUsername] = useInputState(user.username ?? '');
  const [currentPassword, setCurrentPassword] = useInputState('');
  const [password, setPassword] = useState('');
  const [newsletter, setNewsletter] = useCheckboxState(user.newsletter);
  const [email, setEmail] = useInputState(user.email);
  const currentPasswordInput = useRef<HTMLInputElement>(null);

  const pendingEmailChangeRequest = useFetcher(async () => {
    return await api.pendingEmailChange(user.id);
  }, [user.id]);

  const form = useForm(async () => {
    await api.updateUser(user.id, {
      username,
      password: password === '' ? null : password,
      currentPassword,
      email: email === '' ? null : email,
      newsletter,
    });

    toast.success(ts('your_account_information_has_been'));
    pendingEmailChangeRequest.refresh();

    currentPasswordInput.current!.value = '';
  }, [
    user.id,
    username,
    password,
    currentPassword,
    email,
    newsletter,
    ts,
    pendingEmailChangeRequest,
  ]);

  const pendingEmailChange = pendingEmailChangeRequest.data?.pending;

  return (
    <UserStateComponent>
      <PageHeader>{ts('edit_account_details')}</PageHeader>
      <Row className="justify-content-center">
        <Col lg={6} xs={12}>
          <Card>
            <CardBody className="text-center" id="accountForm">
              <Form onSubmit={form.onSubmit}>
                <FormGroup row>
                  <Label for="userId" md={4} sm={12}>
                    {ts('common.user_id')}
                  </Label>
                  <Col md={8} sm={12}>
                    <Input defaultValue={user.id} disabled id="userId" />
                    <FormText color="muted">{ts('user.account.id.explanation')}</FormText>
                  </Col>
                </FormGroup>
                <hr />
                <FormGroup row>
                  <Label for="username" md={4} sm={12}>
                    {ts('common.username')}
                  </Label>
                  <Col md={8} sm={12}>
                    <Input
                      defaultValue={user.username ?? ''}
                      id="username"
                      name="username"
                      onChange={setUsername}
                    />
                    <FormText color="muted">{ts('current_username_of_the_user')}</FormText>
                  </Col>
                </FormGroup>
                <hr />
                <FormGroup row>
                  <Label for="currentPassword" md={4} sm={12}>
                    {ts('current_password')}
                  </Label>
                  <Col md={8} sm={12}>
                    <Input
                      autoComplete="password"
                      id="currentPassword"
                      innerRef={currentPasswordInput}
                      name="currentPassword"
                      onChange={setCurrentPassword}
                      type="password"
                    />
                    <FormText color="muted">{ts('to_make_any_changes_to')}</FormText>
                  </Col>
                </FormGroup>
                <hr />
                <FormGroup row>
                  <Label for="email" md={4} sm={12}>
                    {ts('common.email_address')}
                  </Label>
                  <Col md={8} sm={12}>
                    <Input
                      autoComplete="email"
                      defaultValue={user.email}
                      disabled={pendingEmailChange}
                      id="email"
                      name="email"
                      onChange={setEmail}
                      type="email"
                    />
                    {user.email !== email && (
                      <Alert className="mt-2" color="warning">
                        <RichMarkdownIt source={ts('note_you_will_need_to')} />
                      </Alert>
                    )}
                    {pendingEmailChange && (
                      <Alert className="mt-2" color="warning" id="pendingEmailChange">
                        <RichMarkdownIt source={ts('note_an_email_change_request')} />
                      </Alert>
                    )}
                  </Col>
                </FormGroup>
                <FormGroup check className="mt-10 start-align">
                  <Input
                    defaultChecked={user.newsletter}
                    id="newsletter"
                    name="newsletter"
                    onChange={setNewsletter}
                    type="checkbox"
                  />
                  <Label check for="newsletter">
                    {ts('i_want_to_receive_the', convention.longName)}
                  </Label>
                </FormGroup>
                <hr />
                <PasswordChangeInput
                  md={8}
                  onChange={setPassword}
                  passwordScore={passwordScore}
                  sm={12}
                />
                <hr />
                <Row className="justify-content-center">
                  <Col className="mt-10" lg={6} xs={12}>
                    <Button block color="primary" id="updateAccountDetails" type="submit">
                      {ts('common.update')}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </UserStateComponent>
  );
};
